import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { projects, ProjectItem } from '../shared/projects';
import { toProjectSlug } from '../utils/projectSlug';
import {
  getProjectDestination,
  getProjectWorkLabel,
  getProjectWorkTitle,
  getProjectWorkDate,
} from '../shared/projectLinks';
import usePageMeta from '../utils/usePageMeta';

const archivedProjects = projects.filter((project) => !project.hideFromHome);

// The archive is a launcher: every entry points at a writeup, a repository, or
// a live site, and all of them open in a new tab so this page stays put.
const WorkLink: React.FC<{
  destination: string;
  className?: string;
  ariaLabel?: string;
  children: React.ReactNode;
}> = ({ destination, className, ariaLabel, children }) => (
  <a
    href={destination}
    className={className}
    aria-label={ariaLabel}
    target="_blank"
    rel="noreferrer"
  >
    {children}
  </a>
);

const ProjectVisual: React.FC<{ project: ProjectItem; index: number }> = ({
  project,
  index,
}) => {
  const [imageFailed, setImageFailed] = React.useState(false);
  const repositoryPath = project.github?.replace('https://github.com/', '');
  const imageSource =
    project.image !== 'image.png'
      ? project.image.startsWith('http')
        ? project.image
        : `/${project.image}`
      : repositoryPath
        ? `https://opengraph.githubassets.com/portfolio-${toProjectSlug(project)}/${repositoryPath}`
        : undefined;

  if (imageSource && !imageFailed) {
    return (
      <img
        src={imageSource}
        alt=""
        loading="lazy"
        className="project-entry__image"
        onError={() => setImageFailed(true)}
      />
    );
  }

  return (
    <div
      className={`project-entry__placeholder project-entry__placeholder--${
        (index % 4) + 1
      }`}
      aria-hidden="true"
    >
      <span>{String(index + 1).padStart(2, '0')}</span>
      <strong>{project.title}</strong>
      <small>{project.technologies.slice(0, 2).join(' / ')}</small>
    </div>
  );
};

const WorkDetail: React.FC = () => {
  usePageMeta({
    title: 'Work',
    description:
      'Writing, experiments, and projects by Vividh Mahajan on computer use and infrastructure for AI agents.',
    path: '/work',
  });

  return (
    <main className="work-page project-archive">
      <header className="work-nav">
        <Link to="/" className="work-nav__brand">
          Vividh Mahajan
        </Link>
        <nav aria-label="Work page navigation">
          <Link to="/">Home</Link>
          <a href="https://github.com/Lasdw6" target="_blank" rel="noreferrer">
            GitHub
          </a>
          <a href="/resume.pdf" target="_blank" rel="noreferrer">
            Résumé
          </a>
        </nav>
      </header>

      <motion.div
        className="work-page__inner"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      >
        <header className="work-intro">
          <h1>Work</h1>
          <p>Writing, experiments, and projects.</p>
        </header>

        <ol className="work-grid">
          {archivedProjects.map((project, index) => {
            const projectSlug = toProjectSlug(project);
            const destination = getProjectDestination(project);
            const workLabel = getProjectWorkLabel(project);

            return (
              <motion.li
                className="work-item"
                key={projectSlug}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{
                  duration: 0.45,
                  delay: (index % 4) * 0.04,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                <WorkLink
                  className="work-item__preview-link project-entry__visual"
                  destination={destination}
                  ariaLabel={`Open ${project.title}`}
                >
                  <ProjectVisual project={project} index={index} />
                </WorkLink>

                <div className="work-item__heading">
                  <h2>
                    <WorkLink destination={destination}>
                      {getProjectWorkTitle(project)}
                    </WorkLink>
                  </h2>
                </div>

                <p className="work-item__meta">
                  {getProjectWorkDate(project)}, {workLabel}
                </p>
                <p className="work-item__description">
                  {project.shortDescription || project.description}
                </p>
              </motion.li>
            );
          })}
        </ol>
      </motion.div>
    </main>
  );
};

export default WorkDetail;
